import styled from "styled-components";

import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import ButtonRoundIcon from "components/buttons/ButtonRoundIcon";
import ToggleSwitch from "components/forms/ToggleSwitch";
import InputTextSmall from "components/forms/InputTextSmall";
import ButtonPrimarySmall from "components/buttons/ButtonPrimarySmall";

import blockIcon from "assets/img/icons/icons8-unavailable.svg";
import pencilIcon from "assets/img/icons/pencil-icon.svg";
import pencilWhiteIcon from "assets/img/icons/pencil-icon-white.svg";
import mailPreviewIcon from "assets/img/icons/mail-preview-icon.svg";
import mailPreviewWhiteIcon from "assets/img/icons/mail-preview-icon-white.svg";
import copyIcon from "assets/img/icons/copy-icon.svg";
import copyWhiteIcon from "assets/img/icons/copy-icon-white.svg";
import binIcon from "assets/img/icons/bin-icon.svg";
import binWhiteIcon from "assets/img/icons/bin-icon-white.svg";
import calendarEditIcon from "assets/img/icons/calendar-edit-icon.svg";
import calendarEditWhiteIcon from "assets/img/icons/calendar-edit-icon-white.svg";

const AutoresponderMessages = styled.div``;

const Header = styled.div`
  margin-bottom: 12px;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 500;
`;

const Messages = styled.div`
  width: 100%;
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  justify-content: space-between;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
`;

const TableWrap = styled.div`
  width: 100%;
  padding-bottom: 5px;
  margin-bottom: -5px;

  overflow-x: auto;
`;

const ListTable = styled.table`
  width: 100%;
  min-width: 520px;

  border-spacing: 0;
`;

const TableHead = styled.thead``;

const Label = styled.th`
  padding: ${(props) => (props.padding ? props.padding : "0 5px 12px 5px")};
  max-width: ${(props) => (props.width ? props.width : "none")};

  font-size: 17px;
  font-weight: 400;
  text-align: left;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const LabelText = styled.div``;

const LabelButton = styled.div`
  transform: translateY(1px);
`;

const TableBody = styled.tbody``;

const Cell = styled.td`
  padding: ${(props) => (props.smallpadding ? "2px 4px" : "8px 4px")};
  max-width: ${(props) => (props.width ? props.width : "none")};

  white-space: nowrap;
  font-size: 15px;
`;

const CellLineValueWrap = styled.div`
  padding-right: 10px;

  display: flex;
  align-items: center;
  gap: 6px;
`;

const NoResults = styled.div`
  width: 100%;
  margin: 25px 0;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const NoResultsIcon = styled.div`
  width: 32px;
  min-width: 32px;
  height: 32px;
`;

const NoResultsText = styled.div``;

const EllipsisValue = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const InfoText = styled.div``;

export default ({
  data,
  onStatusToggleClick,
  loadingStatusToggle,
  onEditClick,
  onPreviewClick,
  onDeleteClick,
  onDuplicateClick,
  onEditOrderClick,
  editOrderActive,
  onOrderInputChange,
  orderWarnings,
  schedule,
  updateSchedule,
}) => {
  const list = data.responderMessages.map((message) => {
    const { id, order, status, subject, sent_emails_aggregate } = message;

    return (
      <tr key={id}>
        {editOrderActive && (
          <Cell smallpadding={1}>
            <InputTextSmall
              type="text"
              value={schedule[id]}
              onChange={(e) => onOrderInputChange(id, e)}
              warning={orderWarnings.includes(id)}
              maxLength="3"
              width="40px"
            />
          </Cell>
        )}
        {!editOrderActive && <Cell>{order}</Cell>}
        <Cell width="200px">
          <CellLineValueWrap>
            <EllipsisValue>{subject}</EllipsisValue>
          </CellLineValueWrap>
        </Cell>
        <Cell>
          <ButtonRoundIcon
            icon={mailPreviewIcon}
            hoverIcon={mailPreviewWhiteIcon}
            background={css.BRAND_YELLOW}
            hoverBackground={css.BRAND_GREEN}
            onClick={() => onPreviewClick(id)}
            buttonSize="24px"
            iconSize="12px"
            transform="translateY(1px)"
          />
        </Cell>
        <Cell>{sent_emails_aggregate.aggregate.count}</Cell>
        <Cell>
          <ToggleSwitch
            on={status === "active"}
            onClick={() => onStatusToggleClick(id)}
            loading={loadingStatusToggle}
          />
        </Cell>
        <Cell></Cell>
        <Cell width="85px">
          <Controls>
            <ButtonRoundIcon
              icon={pencilIcon}
              hoverIcon={pencilWhiteIcon}
              background={css.BRAND_YELLOW}
              hoverBackground={css.BRAND_GREEN}
              onClick={() => onEditClick(id)}
              buttonSize="24px"
              iconSize="12px"
            />
            <ButtonRoundIcon
              icon={copyIcon}
              hoverIcon={copyWhiteIcon}
              background={css.BRAND_YELLOW}
              hoverBackground={css.BRAND_GREEN}
              onClick={() => onDuplicateClick(id)}
              buttonSize="24px"
              iconSize="12px"
            />
            <ButtonRoundIcon
              icon={binIcon}
              hoverIcon={binWhiteIcon}
              background={css.BRAND_YELLOW}
              hoverBackground={css.BRAND_GREEN}
              onClick={() => onDeleteClick(id)}
              buttonSize="24px"
              iconSize="12px"
            />
          </Controls>
        </Cell>
      </tr>
    );
  });

  return (
    <AutoresponderMessages>
      <Header>
        <Title>Email Messages</Title>
      </Header>
      <Messages>
        <InfoText>
          The autoresponder will send the following email messages to your
          subscribers on the specified days. The day 0 message is a welcome
          message sent immediately after the subscriber is verified. Click the
          calendar icon to change the sending schedule.
        </InfoText>
        <TableWrap className="table-wrap">
          {list.length > 0 && (
            <ListTable>
              <TableHead>
                <tr>
                  {!editOrderActive && (
                    <Label width="80px">
                      <LabelRow>
                        <LabelText>Day</LabelText>
                        <LabelButton>
                          <ButtonRoundIcon
                            icon={calendarEditIcon}
                            hoverIcon={calendarEditWhiteIcon}
                            background={css.BRAND_YELLOW}
                            hoverBackground={css.BRAND_GREEN}
                            onClick={onEditOrderClick}
                            buttonSize="24px"
                            iconSize="12px"
                          />
                        </LabelButton>
                      </LabelRow>
                    </Label>
                  )}
                  {editOrderActive && (
                    <Label padding="0 5px 8px 5px" width="80px">
                      <ButtonPrimarySmall onClick={updateSchedule}>
                        Save
                      </ButtonPrimarySmall>
                    </Label>
                  )}
                  <Label>Subject</Label>
                  <Label>Preview</Label>
                  <Label>Sent</Label>
                  <Label>Status</Label>
                  <Label></Label>
                </tr>
              </TableHead>
              <TableBody>{list}</TableBody>
            </ListTable>
          )}
          {list.length === 0 && (
            <NoResults>
              <NoResultsIcon>
                <img src={blockIcon} style={{ width: "100%" }} />
              </NoResultsIcon>
              <NoResultsText>
                The autoresponder does not have any messages created yet.
              </NoResultsText>
            </NoResults>
          )}
        </TableWrap>
      </Messages>
    </AutoresponderMessages>
  );
};
